import React, { useState, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";

import { useNavigate } from "react-router-dom";

import "./UserTable.css";

import userService from '../../services/userService';
import storage from '/src/utils/storage';
import { formatDateWithoutSeconds } from '/src/utils/dateWithoutSeconds';
import { useToastDispatchContext } from '/src/layouts/toast/context/toastContext';
import { PageTable, Header } from "/src/layouts/page";
import IconButton from "/src/components/UI/Button/IconButton/IconButton";
import hasPermission from "/src/utils/hasPermission";

function UserTable() {
    const currentUser = storage("local").get("user");
    const isAdmin = !!currentUser.is_admin;
    const canAddUser = isAdmin || hasPermission('add_user');
    const canUpdateUser = isAdmin || hasPermission('update_user');
    const canDeleteUser = isAdmin || hasPermission('delete_user');
    const { getUsers, deleteUser, resetPassword } = userService;
    const toast = useToastDispatchContext();
    const navigate = useNavigate();

    const [gridApi, setGridApi] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const passwordResetLocked = useRef(false);

    const getUsersCallable = async (params) => {
        return await getUsers(params);
    };

    const datasource = useMemo(() => ({
        getRows: async (params) => {
            setIsLoading(true);
            const startRow = params.startRow;
            const endRow = params.endRow;
            const limit = endRow - startRow;
            const page = Math.floor(startRow / limit) + 1;

            // Infinite row model: the grid never re-sorts fetched rows itself,
            // the datasource must ask the backend for a sorted page.
            const sortModel = params.sortModel?.[0];
            const [response, error] = await getUsersCallable({
                page,
                limit,
                ...(sortModel ? { sort_by: sortModel.colId, sort_dir: sortModel.sort } : {})
            });

            if (error) {
                toast.error("Error Fetch", "Unable to reach data, see network section for more info");
                params.failCallback();
                setIsLoading(false);
                return;
            }

            params.successCallback(response.data || [], response.total || 0);
            setIsLoading(false);
        }
    }), []);

    const onClickAdd = () => navigate("/administration/user/create");
    const onClickUpdate = (row) => navigate("/administration/user/update", { state: { data: row, type: "update" } });
    const onClickDelete = async (row) => {
        const { id, email } = row;
        await ModalManager.show(ApiModal, {
            title: "Delete user " + email,
            body: "Do you really want to delete user " + email,
            api: {
                request: deleteUser,
                param: [id],
            },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Delete", type: "submit" },
            ],
        }).then((res) => {
            if (res.error != null) {
                toast.error("Error", res.error);
                return;
            }
            gridApi?.purgeInfiniteCache();
        });
    };

    const onClickSendPassword = async (row) => {
        if (passwordResetLocked.current) return;

        await ModalManager.show(ApiModal, {
            title: "Reset password for " + row.email,
            body: "Do you really want to reset the password for " + row.email + "? A new password will be sent to their email address.",
            api: {
                request: resetPassword,
                param: [row.id],
            },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Reset", type: "submit" },
            ],
            confirm: {
                name: row.email,
                fieldName: "the user's email",
            },
        }).then((res) => {
            if (res?.error != null) {
                toast.error("Error", res.error);
                return;
            }

            passwordResetLocked.current = true;
            toast.success("Password Reset", "The user will get a new password on their email address");
            setTimeout(() => { passwordResetLocked.current = false; }, 4000);
        });
    };

    const columnDefs = [
        {
            headerName: "EMAIL",
            field: "email",
            flex: 1,
            cellRenderer: (params) => (
                <span
                    style={{ color: "var(--main)", cursor: "pointer" }}
                    onClick={() =>
                        navigator.clipboard.writeText(params.value).then(() => {
                            toast.success("Copy to clipboard", "You can paste this email where you need !", 5000);
                        })
                    }>
                    {params.value}
                </span>
            )
        },
        {
            headerName: "ROLE",
            field: "roles",
            width: 160,
            sortable: false,
            valueFormatter: (params) => (params.value?.length ? params.value.join(", ") : "No role")
        },
        {
            headerName: "POLE",
            field: "pole_name",
            width: 140,
            sortable: false,
            valueFormatter: (params) => params.value || ""
        },
        {
            headerName: "LEADER",
            field: "pole_leaders",
            width: 160,
            sortable: false,
            // Blank when the user isn't a pole agent, or their pole has no
            // leader - a pole leader themselves never has a pole_id (can't
            // be both a leader and an agent), so this never applies to them.
            valueFormatter: (params) => (params.value?.length ? params.value.join(", ") : "")
        },
        {
            headerName: "LAST CONNECTION",
            field: "last_login_at",
            width: 200,
            valueFormatter: (params) => params.value ? formatDateWithoutSeconds(params.value, true) : "/"
        },
        (canUpdateUser || canDeleteUser) ? {
            headerName: "Actions",
            width: 150,
            pinned: "right",
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                if (params.data?.id === currentUser.id) return null;
                return (
                    <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                        {canUpdateUser && <i className="fas fa-edit action-button blue" onClick={() => onClickUpdate(params.data)} />}
                        {canUpdateUser && <i className="fas fa-sync-alt action-button blue" onClick={() => onClickSendPassword(params.data)} />}
                        {canDeleteUser && <i className="fas fa-trash action-button red" onClick={() => onClickDelete(params.data)} />}
                    </div>
                );
            }
        } : null,
    ].filter(Boolean);

    return (
        <PageTable>
            <Header title="Manage Users"></Header>
            <div className="user-table">
                <div className="user-admin-toolbar">
                    {canAddUser && (
                        <IconButton
                            className="main-iconButton profil_action user-admin-add-button"
                            icon="fas fa-plus"
                            onClick={onClickAdd}
                            title="Add user"
                        />
                    )}
                </div>
                <div className="ag-theme-alpine" style={{ width: "100%", height: "650px" }}>
                    <AgGridReact
                        rowModelType="infinite"
                        datasource={datasource}
                        cacheBlockSize={20}
                        pagination={true}
                        paginationPageSize={20}
                        columnDefs={columnDefs}
                        defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        loading={isLoading}
                        onGridReady={(params) => setGridApi(params.api)}
                        getRowStyle={(params) => {
                            if (!params.data) return { opacity: 0 };
                        }}
                    />
                </div>
            </div>
        </PageTable>
    );
}

export default UserTable;
